import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { BookSearchBook, BookSearchUI } from '../../types';

const PAGE_SIZE = 10;

const token = {
  /** 도서검색결과 라벨과 동일 (번호·카드 왼쪽 액센트) */
  brand: '#22468A',
  available: '#16a34a',
  onLoan: '#d97706',
  reserve: '#3b82f6',
  reserveNo: '#dc2626',
  unavail: '#94a3b8',
  text: '#111827',
  sub: '#6b7280',
  muted: '#9ca3af',
  border: '#e5e7eb',
  bg: '#ffffff',
  guideBg: '#f9fafb',
  hover: '#f9fafb',
};

export interface BookSearchResultProps {
  ui: BookSearchUI;
  books?: BookSearchBook[];
  afterContent?: string;
  renderText: (text: string) => React.ReactNode;
  showActions: boolean;
  playTTSLabel: string;
  stopTTSLabel: string;
  isPlayingTTS?: boolean;
  playTextToSpeech?: (text: string) => void;
  stopTextToSpeech?: () => void;
  ttsText: string;
}

type CardStatus = 'available' | 'on-loan' | 'unavailable';

function inferReservable(book: BookSearchBook): boolean {
  if (typeof book.reservable === 'boolean') return book.reservable;
  return (book.reserve_status || '').includes('예약 가능');
}

function inferReturnDate(book: BookSearchBook): string | undefined {
  if (book.return_date) return book.return_date;
  const m = (book.loan_status || '').match(/반납예정일[:\s]*([0-9./-]+)/);
  return m?.[1];
}

function cardStatus(book: BookSearchBook): CardStatus {
  const available = !!book.available;
  const reservable = inferReservable(book);
  if (available) return 'available';
  if (reservable) return 'on-loan';
  return 'unavailable';
}

/** legacy book_cover 카드를 BookSearchBook 형태로 정규화 */
export function normalizeLegacyBook(book: any, idx: number): BookSearchBook {
  const num = book.index ?? idx + 1;
  const reserveStatus = book.reserve_status || '';
  const reservable =
    typeof book.reservable === 'boolean'
      ? book.reservable
      : reserveStatus.includes('예약 가능');
  return {
    id: String(book.id || book.bookkey || ''),
    index: num,
    title: book.title || `도서 ${num}`,
    author: book.author || '',
    cover_image: book.cover_image || book.url || null,
    available: book.available ?? book.is_available,
    reservable,
    return_date: book.return_date || undefined,
    location: book.location || book.shelf_loc || '',
    call_number: book.call_number || book.call_no || '',
    library_name: book.library_name || '',
    loan_status: book.loan_status || '',
    reserve_status: reserveStatus,
    publisher: book.publisher || '',
    pub_year: book.pub_year || '',
  };
}

const BookCard: React.FC<{ book: BookSearchBook; showIndex?: boolean }> = ({
  book,
  showIndex = true,
}) => {
  const status = cardStatus(book);
  const available = !!book.available;
  const reservable = inferReservable(book);
  const returnDate = inferReturnDate(book);
  const num = book.index;
  const imgUrl = (book.cover_image && String(book.cover_image).trim()) || '';
  const lib = (book.library_name || '').trim();
  const titleLine = lib ? `[${lib}] ${book.title}` : book.title;
  const callNo =
    book.call_number && String(book.call_number) !== 'N/A'
      ? String(book.call_number)
      : '';
  const publisher = (book.publisher || '').trim();

  return (
    <Card $status={status}>
      {showIndex && <IndexNum>{num}</IndexNum>}
      <Cover>
        {imgUrl ? (
          <img
            src={imgUrl}
            alt=""
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <CoverPlaceholder>표지 없음</CoverPlaceholder>
        )}
      </Cover>
      <Meta>
        <Title title={titleLine}>{titleLine}</Title>

        <TwoCol>
          <Col>{book.author ? `저자: ${book.author}` : null}</Col>
          <Col>{publisher ? `출판사: ${publisher}` : null}</Col>
        </TwoCol>

        <TwoCol>
          <Col>{book.location ? `자료 위치: ${book.location}` : null}</Col>
          <Col>{callNo ? `청구 기호: ${callNo}` : null}</Col>
        </TwoCol>

        <StatusTwoCol>
          <LoanGroup>
            {available ? (
              <StatusDot $color={token.available}>대출 가능</StatusDot>
            ) : (
              <>
                <StatusDot $color={token.onLoan}>대출중</StatusDot>
                {returnDate && <ReturnDate>반납예정일: {returnDate}</ReturnDate>}
              </>
            )}
          </LoanGroup>
          {reservable ? (
            <StatusDot $color={token.reserve}>예약 가능</StatusDot>
          ) : (
            <StatusDot $color={token.reserveNo}>예약 불가</StatusDot>
          )}
        </StatusTwoCol>
      </Meta>
    </Card>
  );
};

export { BookCard };

/** 전체관: 도서관명 주인공, 대출/예약 배지 없음 */
const HoldingsCard: React.FC<{ book: BookSearchBook; showIndex?: boolean }> = ({
  book,
  showIndex = true,
}) => {
  const num = book.index;
  const imgUrl = (book.cover_image && String(book.cover_image).trim()) || '';
  const lib = (book.library_name || '').trim() || '도서관';
  const publisher = (book.publisher || '').trim();
  const year = (book.pub_year || '').trim();
  const pubLine = [publisher, year ? `(${year})` : ''].filter(Boolean).join(' ');

  return (
    <HoldingsCardBox>
      {showIndex && <IndexNum>{num}</IndexNum>}
      <Cover>
        {imgUrl ? (
          <img
            src={imgUrl}
            alt=""
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <CoverPlaceholder>표지 없음</CoverPlaceholder>
        )}
      </Cover>
      <Meta>
        <LibraryName>{lib}</LibraryName>
        <Title title={book.title}>{book.title}</Title>
        <TwoCol>
          <Col>{book.author ? `저자: ${book.author}` : null}</Col>
          <Col>{pubLine ? `출판: ${pubLine}` : null}</Col>
        </TwoCol>
      </Meta>
    </HoldingsCardBox>
  );
};

const BookSearchResult: React.FC<BookSearchResultProps> = ({
  ui,
  books: booksProp,
  afterContent,
  renderText,
  showActions,
  playTTSLabel,
  stopTTSLabel,
  isPlayingTTS,
  playTextToSpeech,
  stopTextToSpeech,
  ttsText,
}) => {
  const rawBooks =
    (ui.books && ui.books.length > 0 ? ui.books : booksProp) || [];
  const books = rawBooks.map((b, i) => normalizeLegacyBook(b, i));
  const after = afterContent ?? ui.after_content ?? '';
  const isHoldings = ui.mode === 'holdings';
  const [page, setPage] = useState(0);

  const pageCount = Math.max(1, Math.ceil(books.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageBooks = useMemo(
    () => books.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE),
    [books, safePage],
  );
  const showPager = books.length > PAGE_SIZE;
  const from = books.length === 0 ? 0 : safePage * PAGE_SIZE + 1;
  const to = Math.min((safePage + 1) * PAGE_SIZE, books.length);

  return (
    <Container>
      <SectionLabel>{isHoldings ? '소장 도서관' : '도서검색결과'}</SectionLabel>

      <CardList>
        {pageBooks.map((book, idx) =>
          isHoldings ? (
            <HoldingsCard
              key={`lib-hold-${book.index}-${book.id || idx}`}
              book={book}
            />
          ) : (
            <BookCard
              key={`lib-book-${book.index}-${book.id || idx}`}
              book={book}
            />
          ),
        )}
      </CardList>

      {showPager && (
        <PagerRow>
          <PagerButton
            type="button"
            aria-label="이전"
            disabled={safePage <= 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ‹
          </PagerButton>
          <PagerMeta>
            {from}–{to} / {books.length}
          </PagerMeta>
          <PagerButton
            type="button"
            aria-label="다음"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          >
            ›
          </PagerButton>
        </PagerRow>
      )}

      {after && <AfterBooks>{renderText(after)}</AfterBooks>}

      {showActions && (
        <ButtonRow>
          {playTextToSpeech && (
            <TTSButton
              onClick={() => {
                if (isPlayingTTS && stopTextToSpeech) {
                  stopTextToSpeech();
                } else {
                  playTextToSpeech(ttsText);
                }
              }}
              $isPlaying={isPlayingTTS}
            >
              {isPlayingTTS ? stopTTSLabel : playTTSLabel}
            </TTSButton>
          )}
        </ButtonRow>
      )}
    </Container>
  );
};

export default BookSearchResult;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  margin-top: 0.5rem;
  gap: 8px;
  font-family: 'Noto Sans KR', sans-serif;
  /* 헤더 AiBubble과 동일 본문 크기 */
  font-size: inherit;
  line-height: inherit;
  font-weight: 400;

  @media (max-width: 768px) {
    margin-top: 0.35rem;
    gap: 6px;
  }
`;

const SectionLabel = styled.p`
  font-family: inherit;
  font-size: inherit;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${(p) => (p.theme.textDefaultColor === '#FFF' ? token.muted : token.brand)};
  margin: 0;
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  min-width: 0;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const HoldingsCardBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  background: ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#1e293b' : token.bg)};
  border: 1px solid ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#334155' : token.border)};
  border-left: 3px solid
    ${(p) => (p.theme.textDefaultColor === '#FFF' ? token.muted : token.brand)};
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  &:hover {
    background: ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#273549' : token.hover)};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 768px) {
    gap: 8px;
    padding: 10px 10px;
    border-radius: 10px;
    align-items: center;
  }
`;

const LibraryName = styled.div`
  font-family: inherit;
  font-size: 1.05em;
  font-weight: 700;
  color: ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#e2e8f0' : token.brand)};
  margin: 0 0 4px;
  line-height: 1.35;
`;

const PagerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 2px 0 4px;
`;

const PagerButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#475569' : token.border)};
  background: ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#1e293b' : token.bg)};
  color: ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#e2e8f0' : token.brand)};
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    background: ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#273549' : token.hover)};
  }
`;

const PagerMeta = styled.span`
  font-size: 0.9em;
  color: ${(p) => (p.theme.textDefaultColor === '#FFF' ? token.muted : token.sub)};
  font-variant-numeric: tabular-nums;
  min-width: 5.5em;
  text-align: center;
`;

const Card = styled.div<{ $status: CardStatus }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  position: relative;
  transition: background 0.15s, box-shadow 0.15s;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#1e293b' : token.bg)};
  border: 1px solid ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#334155' : token.border)};
  border-left: 3px solid
    ${(p) => (p.theme.textDefaultColor === '#FFF' ? token.muted : token.brand)};
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  ${({ $status }) =>
    $status === 'unavailable' &&
    css`
      opacity: 0.55;
    `}

  &:hover {
    background: ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#273549' : token.hover)};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 768px) {
    gap: 8px;
    padding: 10px 10px;
    border-radius: 10px;
    align-items: center;
  }
`;

const IndexNum = styled.span`
  flex-shrink: 0;
  width: 1.25em;
  font-family: inherit;
  font-size: inherit;
  font-weight: 700;
  color: ${(p) => (p.theme.textDefaultColor === '#FFF' ? token.muted : token.brand)};
  padding-top: 0.1em;
  text-align: right;
  align-self: flex-start;
  line-height: 1.4;

  @media (max-width: 768px) {
    width: 1.1em;
    font-size: 0.95em;
    padding-top: 0;
    align-self: center;
  }
`;

/* 모든 표지 동일 슬롯: 책 표지 관례 비율 2:3 (72×108 / 모바일 56×84) */
const Cover = styled.div`
  flex-shrink: 0;
  width: 72px;
  height: 108px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  overflow: hidden;
  background: ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#334155' : '#f3f4f6')};
  border: 1px solid ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#475569' : token.border)};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  @media (max-width: 768px) {
    width: 56px;
    height: 84px;
  }
`;

const CoverPlaceholder = styled.div`
  font-family: inherit;
  font-size: 0.75em;
  color: ${token.muted};
  text-align: center;
  padding: 0.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const Meta = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: inherit;
  font-size: inherit;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media (max-width: 768px) {
    gap: 3px;
  }
`;

const Title = styled.span`
  font-family: inherit;
  font-size: inherit;
  font-weight: 600;
  color: ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#f8fafc' : token.text)};
  line-height: 1.4;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;

  @media (max-width: 768px) {
    font-size: 0.98em;
    line-height: 1.35;
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 12px;
  align-items: start;
  min-width: 0;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 2px;
  }
`;

const StatusTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 12px;
  align-items: center;
  min-width: 0;
  margin-top: 2px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    align-items: start;
    gap: 2px;
  }
`;

const Col = styled.div`
  min-width: 0;
  font-family: inherit;
  font-size: inherit;
  color: ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#94a3b8' : token.sub)};
  line-height: 1.4;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 0.92em;
  }
`;

const LoanGroup = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  min-width: 0;
  min-height: 1.4em;

  @media (max-width: 768px) {
    gap: 4px;
  }
`;

const StatusDot = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: inherit;
  font-size: inherit;
  font-weight: 500;
  line-height: 1.4;
  color: ${({ $color }) => $color};
  min-width: 0;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: 0.92em;
    gap: 4px;
  }
`;

const ReturnDate = styled.span`
  font-family: inherit;
  font-size: inherit;
  line-height: 1.4;
  color: ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#94a3b8' : token.muted)};
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 0.9em;
    white-space: normal;
  }
`;

const AfterBooks = styled.div`
  margin: 0.2rem 0 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  color: ${(p) => p.theme.textDefaultColor};
  white-space: pre-wrap;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  padding: 0;

  a {
    color: ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#60A5FA' : token.brand)};
    text-decoration: underline;
    word-break: break-all;
  }

  strong,
  b {
    font-weight: 700;
    color: ${(p) => (p.theme.textDefaultColor === '#FFF' ? '#FFF' : token.brand)};
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.25rem;
  align-items: center;
  justify-content: flex-start;
`;

const sharedButtonStyles = `
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  min-height: 2.8rem;
  line-height: 1.4;
  font-family: inherit;
  font-size: 1.3rem;
`;

const TTSButton = styled.button<{ $isPlaying?: boolean }>`
  ${sharedButtonStyles}
  padding: 0.5rem 1rem;
  background: ${(p) =>
    p.$isPlaying
      ? 'linear-gradient(135deg, #e74c3c, #c0392b)'
      : 'linear-gradient(135deg,rgb(102, 126, 234),rgb(118, 75, 162))'};
  color: white;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;

  ${(p) =>
    p.$isPlaying &&
    css`
      animation: pulse 1.5s ease-in-out infinite alternate;
      @keyframes pulse {
        0% {
          box-shadow: 0 0 0 0 rgba(231, 76, 60, 0.7);
        }
        100% {
          box-shadow: 0 0 0 10px rgba(231, 76, 60, 0);
        }
      }
    `}
`;
